/**
 * m.2.2 Ekonomski P&L drill: Grupa > Tvrtka > Sektor > Poslovnica > Ponuda > COGS segmenti s dokazom.
 * Podaci: /api/pl-cube/profit (P&L po jednoj osi), /api/pl-cube/cube (matrica poslovnica x sektor),
 * /api/pl-cube/facts (redci ponude) i /api/pl-cube/event (dokaz jednog retka).
 * Crno-bijelo, Arial, negativno s minusom, bez boja i sjena. Esc = razina gore.
 *
 * m.2.4: uz prop `derived` iste osi rade nad izvedenim dimenzijama kocke (sektor/kanal/metal/mt iz
 * Layers::keyExpr) umjesto sirovih (business_type/branch_name); drill tvrtka -> sektor -> MT -> dokument.
 * Zadano `derived=false` cuva 2.2 ponasanje netaknutim. Tanki modul m.2.4 je plCube24Module.tsx.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../shared/api';

type Locale = 'hr' | 'en';
export type EkonMode = 'knjig' | 'sim_knjig' | 'ekon';
type Dim = 'company_code' | 'business_type' | 'branch_name' | 'region' | 'ym' | 'document' | 'employee_code' | 'mtp_kind' | 'dept' | 'konto3' | 'partner'
    | 'sektor' | 'kanal' | 'metal' | 'mt';
type Tr = (hr: string, en: string) => string;

type Row = {
    key: string | null; prihod: number; cogs: number; bruto: number; bruto_pct: number | null; opex: number; ostalo: number;
    ebitda: number; ebitda_pct: number | null; da: number; n_docs: number; n: number; gap_signed: number; gap_n: number; conf_min: number;
};
type Profit = { by: string; mode: string; year: number; build: { build_id: string; built_at: string } | null; total: Row; rows: Row[]; rest: Row | null; count: number; notes?: string[];
    cmp?: { from: string; to: string; total: Row; rows: Row[] } | null; alloc_applied?: boolean };
/** m.2.4 opseg pogleda: tvrtke (prazno = cijela grupa), slobodni raspon datuma, medudrustveno. */
type IcMode = 'include' | 'eliminate' | 'isolate';
type Scope = { cos: string[]; dFrom: string; dTo: string; ic: IcMode };
type CmpMode = 'none' | 'py' | 'prev' | 'custom';
const COMPANIES: Array<[string, string]> = [['ADO', 'ADO'], ['ADMS', 'ADMS'], ['MONESTIQ', 'Monestiq'], ['ARTEMIS_HOLDING', 'ADG']];
type Step = { dim: Dim; key: string | null };
type Fact = {
    id: number; company_code: string; doc_date: string | null; pl_line: string; business_type: string | null; branch_name: string | null; partner: string | null;
    document: string | null; source_table: string; source_id: number; source_ref: string | null; signed: number; rule_code: string | null; confidence: number;
    origin: string; coverage: string; gap_reason: string | null; konto?: string | null;
    derived?: { sektor: string | null; kanal: string | null; metal: string | null; mt: string | null };
};
type Facts = { rows: Fact[]; n: number; docs: number; signed: number };
/** m.2.4 drawer: razrada jednog kljuca osi (linije P&L po mjesecu, konta, najveci redci). */
type Drawer = { key: string | null; row: Row; lines: Cube | null; konta: Cube | null; facts: Facts | null; err: string | null };
const SORT_PARAM: Record<SortCol, string> = {
    key: 'key', prihod: 'prihod', cogs: 'cogs', bruto: 'bruto', bruto_pct: 'bruto_pct', opex: 'opex', ebitda: 'ebitda', ebitda_pct: 'ebitda_pct', n_docs: 'n_docs', gap_signed: 'gap',
};
const LINE_LABELS: Record<string, [string, string]> = {
    prihod: ['Prihod', 'Revenue'], cogs: ['COGS', 'COGS'], opex: ['Opex', 'Opex'], prijenos_491: ['Prijenos troska 491', 'Cost transfer 491'],
    prihod_ostali: ['Ostali prihod', 'Other income'], stonex: ['StoneX', 'StoneX'], da: ['Amortizacija', 'D&A'], capex: ['Capex', 'Capex'],
    prihod_fin: ['Financijski prihod', 'Financial income'], udjeli_06: ['Udjeli', 'Shares'],
};
const LINE_ORDER = ['prihod', 'cogs', 'opex', 'prijenos_491', 'prihod_ostali', 'stonex', 'da', 'prihod_fin', 'capex', 'udjeli_06'];

/** CSV (separator ;, decimalni zarez) za Excel s hrvatskim postavkama. */
function downloadCsv(name: string, header: string[], rows: (string | number | null)[][]): void {
    const cell = (v: string | number | null) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'number') return String(Math.round(v * 100) / 100).replace('.', ',');
        const s = String(v);
        return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = [header, ...rows].map((r) => r.map(cell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
type EventInfo = {
    fact: Record<string, unknown> | null;
    rule: { code: string; title: string; description?: string; mode?: string; gl_prefixes?: string[] } | null;
    expected_posting: { debit: string | null; credit: string | null; amount: number } | null;
    source: Record<string, unknown> | null;
    negated_fact: Record<string, unknown> | null;
    gl_postings_same_document: Array<{ id: number; broj: string | null; konto: string; duguje: number; potrazuje: number; datum_dokumenta?: string; subjekt: string | null }>;
};
type CubeRow = { keys: Record<string, string | null>; n: number; signed: number; gap_n: number; gap_signed: number };
type Cube = { rows: CubeRow[]; truncated?: boolean };

type SortCol = 'key' | 'prihod' | 'cogs' | 'bruto' | 'bruto_pct' | 'opex' | 'ebitda' | 'ebitda_pct' | 'n_docs' | 'gap_signed';

const NULL = '∅';
const DIM_LABELS: Record<Dim, [string, string]> = {
    company_code: ['Tvrtka', 'Company'], business_type: ['Sektor', 'Sector'], branch_name: ['Poslovnica', 'Branch'], region: ['Regija', 'Region'],
    ym: ['Mjesec', 'Month'], document: ['Ponuda / dokument', 'Offer / document'], employee_code: ['Djelatnik', 'Employee'], mtp_kind: ['Vrsta MTP', 'Cost center kind'],
    dept: ['Odjel', 'Department'], konto3: ['Konto (3)', 'Account (3)'], partner: ['Partner', 'Partner'],
    sektor: ['Sektor', 'Sector'], kanal: ['Kanal', 'Channel'], metal: ['Metal', 'Metal'], mt: ['Mjesto troska', 'Cost center'],
};
const NULL_LABELS: Record<Dim, [string, string]> = {
    company_code: ['(bez tvrtke)', '(no company)'], business_type: ['(bez sektora)', '(no sector)'], branch_name: ['Uprava / bez poslovnice', 'HQ / no branch'],
    region: ['(bez regije)', '(no region)'], ym: ['(bez datuma)', '(no date)'], document: ['(bez dokumenta)', '(no document)'], employee_code: ['(bez djelatnika)', '(no employee)'],
    mtp_kind: ['(bez MTP)', '(no cost center)'], dept: ['(bez odjela)', '(no department)'], konto3: ['(bez konta)', '(no account)'], partner: ['(bez partnera)', '(no partner)'],
    sektor: ['(bez sektora)', '(no sector)'], kanal: ['(bez kanala)', '(no channel)'], metal: ['(bez metala)', '(no metal)'], mt: ['Bez MT', 'No cost center'],
};
const GROUP_CHOICES: Dim[] = ['company_code', 'business_type', 'branch_name', 'region', 'ym', 'document', 'employee_code'];
const ALL_DIMS: Dim[] = ['company_code', 'business_type', 'branch_name', 'region', 'ym', 'document', 'employee_code', 'mtp_kind', 'dept', 'konto3', 'partner', 'sektor', 'kanal', 'metal', 'mt'];
const NEXT: Partial<Record<Dim, Dim>> = { company_code: 'business_type', business_type: 'branch_name', branch_name: 'document', region: 'branch_name', ym: 'document', employee_code: 'document', mtp_kind: 'document', dept: 'document', konto3: 'document', partner: 'document' };
const DRILL_ORDER: Dim[] = ['company_code', 'business_type', 'branch_name', 'document'];

// m.2.4: iste osi nad izvedenim dimenzijama kocke (sektor/kanal/metal/mt iz Layers::keyExpr). Drill tvrtka -> sektor -> MT -> dokument.
const GROUP_CHOICES_D: Dim[] = ['company_code', 'sektor', 'kanal', 'metal', 'mt', 'ym', 'document'];
const NEXT_D: Partial<Record<Dim, Dim>> = { company_code: 'sektor', sektor: 'mt', mt: 'document', kanal: 'document', metal: 'document', ym: 'document' };
const DRILL_ORDER_D: Dim[] = ['company_code', 'sektor', 'mt', 'document'];

/** Kopija config('pl_cube.business_type_labels'); nepoznat kljuc se prikazuje sirov. */
const BT_LABELS: Record<string, string> = {
    prodaja_investicijsko: 'Investicijsko zlato', otkup_investicijsko: 'Otkup investicijskog', prodaja_lom_tca: 'Lom zlata (TCA)', otkup_lom: 'Otkup loma',
    mjenjacnica: 'Mjenjacnica', zalog: 'Zalog', stonex: 'StoneX hedging', veleprodaja: 'Veleprodaja', web: 'Web', poslovnica_operativa: 'Poslovnice',
    logistika: 'Logistika', prijevoz: 'Prijevoz', racunovodstvo: 'Racunovodstvo', financije: 'Financije', marketing: 'Marketing', it: 'IT', uprava: 'Uprava',
    prodajni_tim: 'Prodajni tim', uprava_ostalo: 'Uprava ostalo', ic: 'Medudrustveno',
};
/** Kopije config('pl_cube.sectors.labels') i V23\Layers labela; izvedene osi m.2.4. Nepoznat kljuc se prikazuje sirov. */
const SECTOR_LABELS: Record<string, string> = {
    investicijsko: 'Investicijsko zlato', zlatna_stednja: 'Zlatna stednja', lom: 'Lom zlata', mjenjacnica: 'Mjenjacnica', zalog: 'Zalog',
    stonex: 'StoneX hedging', medudrustveno: 'Medudrustveno', posredovanje_adms: 'Posredovanje ADMS', overhead: 'Overhead (nije rasporeden)',
};
const CHANNEL_LABELS: Record<string, string> = {
    hnwi: 'HNWI (Goldman Graff)', veleprodaja: 'Veleprodaja', web: 'Digital (web)', poslovnice: 'Poslovnice', bez_kanala: 'Overhead (Uprava, vozila, ostalo)',
};
const METAL_LABELS: Record<string, string> = {
    AU: 'Zlato', AG: 'Srebro', nepoznato: 'Roba, kovina nepoznata', bez: 'Bez kovine (OPEX i ostalo)',
};
/** MT (Layers::mtExpr): poslovnica sirovo, 'hq:<odjel>' Uprava, 'vozila', 'bez_mt'. */
function mtLabel(k: string): string {
    if (k === 'vozila') return 'Vozila';
    if (k === 'bez_mt') return 'Bez MT';
    if (k.startsWith('hq:')) { const d = k.slice(3); return `Uprava - ${BT_LABELS[d] ?? d}`; }
    return k;
}
const MONTHS: Record<Locale, string[]> = {
    hr: ['sij', 'vlj', 'ozu', 'tra', 'svi', 'lip', 'srp', 'kol', 'ruj', 'lis', 'stu', 'pro'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};
const SEGMENTS: Record<string, [string, string]> = {
    fixing: ['Fixing', 'Fixing'], hedge: ['Hedge', 'Hedge'], besoft: ['BeSoft nabava', 'BeSoft purchase'], aproks: ['Aproksimacija', 'Approximation'],
};
/** Linije koje ulaze u EBITDA u matrici (isto kao iskaz: prihod, cogs, opex, 491, 78, StoneX). */
const EBITDA_LINES = new Set(['prihod', 'cogs', 'opex', 'prijenos_491', 'prihod_ostali', 'stonex']);

const num = (v: unknown) => Number(v) || 0;
/** EUR bez decimala, tisucice tockom, negativno s obicnim minusom (Intl za hr vraca U+2212). */
export function fmtE(v: number): string {
    const r = Math.round(num(v));
    if (r === 0) return '0';
    const s = Math.abs(r).toLocaleString('hr-HR', { maximumFractionDigits: 0 });
    return r < 0 ? `-${s}` : s;
}
function fmtE2(v: number): string {
    const s = Math.abs(num(v)).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num(v) < 0 ? `-${s}` : s;
}
/** KPI: iznad milijuna "4,60 M EUR", inace puni iznos. */
function fmtM(v: number): string {
    const a = Math.abs(num(v));
    if (a < 1_000_000) return `${fmtE(v)} EUR`;
    const s = (a / 1_000_000).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${num(v) < 0 ? '-' : ''}${s} M EUR`;
}
/** Udio kao razlomak (0.0267) -> "2,7 %". */
function fmtP(f: number | null | undefined): string {
    if (f === null || f === undefined || !Number.isFinite(Number(f))) return '-';
    const p = Number(f) * 100;
    const s = Math.abs(p).toLocaleString('hr-HR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return p < 0 ? `-${s} %` : `${s} %`;
}
const normRow = (r: Partial<Row> | null | undefined): Row => ({
    key: r?.key ?? null, prihod: num(r?.prihod), cogs: num(r?.cogs), bruto: num(r?.bruto), bruto_pct: r?.bruto_pct === null || r?.bruto_pct === undefined ? null : num(r.bruto_pct),
    opex: num(r?.opex), ostalo: num(r?.ostalo), ebitda: num(r?.ebitda), ebitda_pct: r?.ebitda_pct === null || r?.ebitda_pct === undefined ? null : num(r.ebitda_pct),
    da: num(r?.da), n_docs: num(r?.n_docs), n: num(r?.n), gap_signed: num(r?.gap_signed), gap_n: num(r?.gap_n), conf_min: r?.conf_min === undefined ? 100 : num(r.conf_min),
});
const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();

function qsOf(year: number, mode: string, path: Step[], from: number, to: number, extra: Record<string, string> = {}, sc?: Scope): string {
    const p = new URLSearchParams({ year: String(year), mode, ...extra });
    if (sc) {
        // 2.4: slobodni raspon, prekidac medudrustveno i izbor tvrtki; korak tvrtke u putanji ima prednost (postavlja se nize)
        p.set('date_from', sc.dFrom); p.set('date_to', sc.dTo); p.set('ic', sc.ic);
        if (sc.cos.length) p.set('f[company_code]', sc.cos.join('|'));
    }
    path.forEach((s) => p.set(`f[${s.dim}]`, s.key === null || s.key === '' ? NULL : s.key));
    if (!sc && (from > 1 || to < 12)) {
        p.set('from', `${year}-${String(from).padStart(2, '0')}-01`);
        p.set('to', `${year}-${String(to).padStart(2, '0')}-${String(lastDay(year, to)).padStart(2, '0')}`);
    }
    return p.toString();
}

function nextDim(cur: Dim, path: Step[], derived = false): Dim {
    if (cur === 'document') return 'document';
    const used = new Set<Dim>([...path.map((s) => s.dim), cur]);
    const n = (derived ? NEXT_D : NEXT)[cur];
    if (n && !used.has(n)) return n;
    return (derived ? DRILL_ORDER_D : DRILL_ORDER).find((d) => !used.has(d)) ?? 'document';
}

/**
 * Zadnji mjesec s ekonomskim COGS-om (ADO hedge i ADMS lom: min(31.08., zadnji StoneX izvod)).
 * Poslije njega ADO nosi prihod po racunu bez COGS-a, pa bi zadani pogled na cijelu godinu
 * napuhao EBITDA (prod 28.09.: 14,40 M za godinu prema 4,61 M za I-VIII). Pomaknuti kad stigne rujanski izvod.
 */
const EKON_DO_MJESECA = 8;
const pad2 = (n: number) => String(n).padStart(2, '0');
const isoDay = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isIso = (v: string | null): v is string => v !== null && /^\d{4}-\d{2}-\d{2}$/.test(v);
const yearEnd = (y: number) => `${y}-${pad2(EKON_DO_MJESECA)}-${pad2(lastDay(y, EKON_DO_MJESECA))}`;
function shiftYear(d: string, by: number): string {
    const y = Number(d.slice(0, 4)) + by; const m = Number(d.slice(5, 7));
    return `${y}-${pad2(m)}-${pad2(Math.min(Number(d.slice(8, 10)), lastDay(y, m)))}`;
}
/** Raspon usporedbe: ista razdoblje prosle godine, prethodno razdoblje iste duljine ili slobodno. */
function cmpRangeOf(m: CmpMode, a: string, b: string, cf: string, ct: string): [string, string] | null {
    if (m === 'none' || !isIso(a) || !isIso(b)) return null;
    if (m === 'custom') return isIso(cf) && isIso(ct) && cf <= ct ? [cf, ct] : null;
    if (m === 'py') return [shiftYear(a, -1), shiftYear(b, -1)];
    const da = new Date(`${a}T00:00:00`); const db = new Date(`${b}T00:00:00`);
    const days = Math.round((db.getTime() - da.getTime()) / 86400000) + 1;
    const end = new Date(da); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - days + 1);
    return [isoDay(start), isoDay(end)];
}
type UrlState = { tab: 'drill' | 'matrica'; by: Dim; path: Step[]; from: number; to: number; d1: string | null; d2: string | null; cos: string[]; ic: IcMode | null; al: boolean | null; cmp: CmpMode | null };

/** Stanje u URL-u (x-prefiks da se ne sudari s ostatkom aplikacije). */
function readUrl(): UrlState {
    const out: UrlState = { tab: 'drill', by: 'company_code', path: [], from: 1, to: EKON_DO_MJESECA, d1: null, d2: null, cos: [], ic: null, al: null, cmp: null };
    try {
        const p = new URLSearchParams(window.location.search);
        if (p.get('xv') === 'matrica') out.tab = 'matrica';
        const by = p.get('xby') as Dim | null;
        if (by && ALL_DIMS.includes(by)) out.by = by;
        const f = p.get('xf');
        if (f) {
            const arr = JSON.parse(f) as Array<[string, string | null]>;
            out.path = arr.filter(([d]) => ALL_DIMS.includes(d as Dim)).map(([d, k]) => ({ dim: d as Dim, key: k }));
        }
        const fr = Number(p.get('xfrom')); const to = Number(p.get('xto'));
        if (fr >= 1 && fr <= 12) out.from = fr;
        if (to >= out.from && to <= 12) out.to = to;
        const d1 = p.get('xd1'); const d2 = p.get('xd2');
        if (isIso(d1) && isIso(d2) && d1 <= d2) { out.d1 = d1; out.d2 = d2; }
        const co = p.get('xco');
        if (co) out.cos = co.split('|').filter((c) => COMPANIES.some(([k]) => k === c));
        const ic = p.get('xic') as IcMode | null;
        if (ic === 'include' || ic === 'eliminate' || ic === 'isolate') out.ic = ic;
        if (p.get('xal') === '0') out.al = false;
        const cm = p.get('xcmp') as CmpMode | null;
        if (cm === 'py' || cm === 'prev') out.cmp = cm;
    } catch { /* neispravan URL: zadano stanje */ }
    return out;
}
function writeUrl(tab: string, by: Dim, path: Step[], from: number, to: number, x?: { sc: Scope; alloc: boolean; cmp: CmpMode }): void {
    try {
        const u = new URL(window.location.href);
        const p = u.searchParams;
        ['xv', 'xby', 'xf', 'xfrom', 'xto', 'xd1', 'xd2', 'xco', 'xic', 'xal', 'xcmp'].forEach((k) => p.delete(k));
        if (x) {
            p.set('xd1', x.sc.dFrom); p.set('xd2', x.sc.dTo);
            if (x.sc.cos.length) p.set('xco', x.sc.cos.join('|'));
            if (x.sc.ic !== 'eliminate') p.set('xic', x.sc.ic);
            if (!x.alloc) p.set('xal', '0');
            if (x.cmp === 'py' || x.cmp === 'prev') p.set('xcmp', x.cmp);
        }
        if (tab !== 'drill') p.set('xv', tab);
        p.set('xby', by);
        if (path.length) p.set('xf', JSON.stringify(path.map((s) => [s.dim, s.key])));
        if (!x && from > 1) p.set('xfrom', String(from));
        if (!x && to !== EKON_DO_MJESECA) p.set('xto', String(to));
        window.history.replaceState(window.history.state, '', u.toString());
    } catch { /* bez URL stanja */ }
}

const css = `
.ek-x { position: fixed; inset: 0; z-index: 4000; background: #fff; color: #111; font-family: Arial, Helvetica, sans-serif; display: flex; flex-direction: column; font-size: 13px; }
.ek-x * { box-sizing: border-box; }
.ek-x button, .ek-x select { background: #fff; border: 1px solid #111; padding: 4px 10px; font: inherit; cursor: pointer; color: #111; border-radius: 0; }
.ek-x input[type=date] { background: #fff; border: 1px solid #111; padding: 3px 6px; font: inherit; color: #111; border-radius: 0; }
.ek-x td.cmp, .ek-x th.cmp { background: #f7f7f7; }
.ek-x button:disabled { color: #999; border-color: #bbb; cursor: default; }
.ek-x button.on { background: #111; color: #fff; }
.ek-x button:focus-visible, .ek-x tr:focus-visible, .ek-x select:focus-visible { outline: 2px solid #111; outline-offset: 1px; }
.ek-x .head { flex: 0 0 auto; border-bottom: 1px solid #111; background: #fff; }
.ek-x .top { display: flex; align-items: center; gap: 16px; padding: 8px 16px; }
.ek-x .top h1 { font-size: 16px; font-weight: normal; letter-spacing: 0.06em; text-transform: uppercase; margin: 0; white-space: nowrap; }
.ek-x .top .grow { flex: 1; }
.ek-x .muted { font-size: 11px; color: #555; }
.ek-x .seg { display: inline-flex; }
.ek-x .seg button { border-right-width: 0; }
.ek-x .seg button:last-child { border-right-width: 1px; }
.ek-x .crumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; padding: 8px 16px; font-size: 15px; border-top: 1px solid #ddd; }
.ek-x .crumbs .c { border: none; padding: 2px 4px; text-decoration: underline; background: none; }
.ek-x .crumbs .c.cur { text-decoration: none; font-weight: bold; cursor: default; }
.ek-x .crumbs .c .d { display: block; font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.04em; text-decoration: none; font-weight: normal; }
.ek-x .crumbs .sep { color: #777; padding: 0 2px; }
.ek-x .ctl { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 20px; padding: 6px 16px 8px; border-top: 1px solid #ddd; }
.ek-x .ctl .lab { font-size: 11px; color: #444; text-transform: uppercase; letter-spacing: 0.04em; margin-right: 6px; }
.ek-x .kpi { display: grid; grid-template-columns: repeat(8, minmax(0, 1fr)); border-bottom: 1px solid #111; flex: 0 0 auto; }
.ek-x .kpi > div { padding: 12px 16px; border-right: 1px solid #ddd; min-width: 0; }
.ek-x .kpi > div:last-child { border-right: none; }
.ek-x .kpi .l { font-size: 11px; color: #444; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ek-x .kpi .v { font-size: 24px; font-variant-numeric: tabular-nums; margin: 4px 0 2px; white-space: nowrap; }
.ek-x .kpi .d { font-size: 11px; color: #333; font-variant-numeric: tabular-nums; }
.ek-x .kpi .help { cursor: help; border-bottom: 1px dotted #111; }
.ek-x .notes { flex: 0 0 auto; padding: 6px 16px; border-bottom: 1px solid #ddd; font-size: 12px; color: #333; line-height: 1.45; }
.ek-x .notes div::before { content: '- '; }
.ek-x .notice { margin: 16px; padding: 12px 16px; border: 1px solid #111; background: #f2f2f2; font-size: 13px; line-height: 1.5; }
.ek-x .body { flex: 1; min-height: 0; display: flex; }
.ek-x .main { flex: 1; min-width: 0; overflow: auto; }
.ek-x .side { flex: 0 0 42%; min-width: 0; overflow: auto; border-left: 1px solid #111; padding: 8px 16px 24px; }
.ek-x .side.drawer h2 { margin-top: 14px; }
.ek-x .side .scrollx { overflow-x: auto; }
.ek-x .side table.dt { width: 100%; font-size: 11px; }
.ek-x .side table.dt td, .ek-x .side table.dt th { padding: 2px 6px; white-space: nowrap; }
.ek-x td.neg { color: #111; font-style: italic; }
.ek-x td.go { width: 1%; padding: 0 6px; }
.ek-x table { border-collapse: collapse; width: 100%; font-variant-numeric: tabular-nums; }
.ek-x th, .ek-x td { padding: 6px 10px; border-bottom: 1px solid #e5e5e5; white-space: nowrap; text-align: right; }
.ek-x th { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #111; font-weight: normal; font-size: 11px; color: #333; z-index: 1; cursor: pointer; user-select: none; }
.ek-x th.nosort { cursor: default; }
.ek-x th.lbl, .ek-x td.lbl { text-align: left; }
.ek-x td.lbl { max-width: 360px; overflow: hidden; text-overflow: ellipsis; }
.ek-x tr.row { cursor: pointer; }
.ek-x tr.row:hover td, .ek-x tr.row:focus td { background: #efefef; }
.ek-x tr.total td { border-top: 1px solid #111; border-bottom: 1px solid #111; font-weight: bold; background: #f4f4f4; }
.ek-x tr.rest td { color: #555; font-style: italic; }
.ek-x tr.sub td { font-weight: bold; border-top: 1px solid #111; }
.ek-x tr.sel td { background: #e0e0e0; }
.ek-x .barcell { width: 160px; min-width: 160px; }
.ek-x .track { position: relative; height: 12px; }
.ek-x .track .zero { position: absolute; top: -2px; bottom: -2px; width: 1px; background: #111; }
.ek-x .track .bar { position: absolute; top: 1px; height: 10px; background: #888; }
.ek-x .track .bar.neg { background: #d4d4d4; border: 1px solid #888; }
.ek-x .tag { display: inline-block; border: 1px solid #111; padding: 0 4px; font-size: 10px; margin-left: 6px; vertical-align: middle; }
.ek-x .tag.inv { background: #111; color: #fff; }
.ek-x h2 { font-size: 12px; font-weight: normal; text-transform: uppercase; letter-spacing: 0.05em; margin: 14px 0 6px; border-bottom: 1px solid #111; padding-bottom: 2px; }
.ek-x .card { padding: 8px 16px 24px; }
.ek-x .card dl, .ek-x .side dl { margin: 0; display: grid; grid-template-columns: 170px 1fr; gap: 3px 10px; font-size: 12px; }
.ek-x dt { color: #555; }
.ek-x dd { margin: 0; word-break: break-word; }
.ek-x .kv td { text-align: left; white-space: normal; word-break: break-word; font-size: 12px; padding: 3px 8px; }
.ek-x .kv td:first-child { color: #555; width: 38%; }
.ek-x .empty { padding: 24px 16px; color: #555; }
.ek-x .small { padding: 1px 8px; font-size: 11px; }
.ek-x .mx td.cell { cursor: pointer; }
.ek-x .mx td.cell:hover { background: #efefef; }
.ek-x .mx td.neg { background: #f0f0f0; }
.ek-x .mx th.lbl { cursor: default; }
`;

type Props = {
    token: string; locale: Locale; year: number; mode: EkonMode; years?: number[];
    onYear?: (y: number) => void; onMode?: (m: EkonMode) => void; onExit: () => void;
    onStatement?: () => void; onCube?: () => void;
    /** m.2.4: grupiraj/drilaj po izvedenim osima kocke (sektor/kanal/metal/mt) umjesto sirovih (business_type/branch_name). */
    derived?: boolean;
};

export function UpravaEkonExplorer({ token, locale, year, mode, years, onYear, onMode, onExit, onStatement, onCube, derived = false }: Props) {
    const t: Tr = useCallback((hr: string, en: string) => (locale === 'en' ? en : hr), [locale]);
    const init = useMemo(readUrl, []);
    const groupChoices = derived ? GROUP_CHOICES_D : GROUP_CHOICES;
    const [tab, setTab] = useState<'drill' | 'matrica'>(init.tab);
    const [by, setBy] = useState<Dim>(derived && !groupChoices.includes(init.by) ? 'company_code' : init.by);
    const [path, setPath] = useState<Step[]>(init.path);
    const [from, setFrom] = useState(init.from);
    const [to, setTo] = useState(init.to);
    const [sort, setSort] = useState<{ col: SortCol; dir: 1 | -1 }>({ col: init.by === 'ym' ? 'key' : 'ebitda', dir: init.by === 'ym' ? 1 : -1 });
    const [data, setData] = useState<Profit | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [facts, setFacts] = useState<Facts | null>(null);
    const [evFact, setEvFact] = useState<Fact | null>(null);
    const [ev, setEv] = useState<EventInfo | null>(null);
    const [evErr, setEvErr] = useState<string | null>(null);
    const [cube, setCube] = useState<Cube | null>(null);
    const [mxMetric, setMxMetric] = useState<'bruto' | 'ebitda' | 'prihod'>('bruto');
    // klik na zaglavlje: sort ide serveru (nad cijelim skupom); dok korisnik ne klikne, server vraca najvece po |EBITDA|
    const [sortTouched, setSortTouched] = useState(false);
    const [drawer, setDrawer] = useState<Drawer | null>(null);
    // m.2.4 opseg: tvrtke, slobodni raspon, medudrustveno, alokacija overheada (os sektor), usporedba razdoblja
    const [cos, setCos] = useState<string[]>(init.cos);
    const [dFrom, setDFrom] = useState(init.d1 ?? `${year}-01-01`);
    const [dTo, setDTo] = useState(init.d2 ?? yearEnd(year));
    const [ic, setIc] = useState<IcMode>(init.ic ?? 'eliminate');
    const [alloc, setAlloc] = useState(init.al ?? true);
    const [cmpMode, setCmpMode] = useState<CmpMode>(init.cmp ?? 'none');
    const [cmpFrom, setCmpFrom] = useState('');
    const [cmpTo, setCmpTo] = useState('');
    const firstYear = useRef(true);
    useEffect(() => {
        // promjena godine u zaglavlju postavlja raspon na tu godinu (prvi render cuva raspon iz URL-a)
        if (firstYear.current) { firstYear.current = false; return; }
        if (derived) { setDFrom(`${year}-01-01`); setDTo(yearEnd(year)); }
    }, [year, derived]);
    const sc = useMemo<Scope | undefined>(() => (derived ? { cos, dFrom, dTo, ic } : undefined), [derived, cos, dFrom, dTo, ic]);
    const cmpR = useMemo(() => (derived ? cmpRangeOf(cmpMode, dFrom, dTo, cmpFrom, cmpTo) : null), [derived, cmpMode, dFrom, dTo, cmpFrom, cmpTo]);

    const docStep = path.find((s) => s.dim === 'document');
    const isOffer = docStep !== undefined;
    const fetchBy: Dim = isOffer ? 'company_code' : by;

    const dimLabel = (d: Dim) => t(DIM_LABELS[d][0], DIM_LABELS[d][1]);
    const keyLabel = useCallback((d: Dim, k: string | null): string => {
        if (k === null || k === '' || k === NULL) return t(NULL_LABELS[d][0], NULL_LABELS[d][1]);
        if (d === 'business_type') return BT_LABELS[k] ?? k;
        if (d === 'sektor') return SECTOR_LABELS[k] ?? k;
        if (d === 'kanal') return CHANNEL_LABELS[k] ?? k;
        if (d === 'metal') return METAL_LABELS[k] ?? k;
        if (d === 'mt') return mtLabel(k);
        if (d === 'ym' && /^\d{4}-\d{2}$/.test(k)) return `${MONTHS[locale][Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;
        return k;
    }, [t, locale]);

    useEffect(() => { writeUrl(tab, by, path, from, to, sc ? { sc, alloc, cmp: cmpMode } : undefined); }, [tab, by, path, from, to, sc, alloc, cmpMode]);

    // P&L po osi (u razini ponude samo total za KPI)
    useEffect(() => {
        if (tab !== 'drill') return undefined;
        let dead = false;
        setLoading(true); setErr(null);
        const sortQs: Record<string, string> = sortTouched ? { sort: SORT_PARAM[sort.col], dir: sort.dir === 1 ? 'asc' : 'desc' } : { sort: 'ebitda' };
        const x: Record<string, string> = {};
        if (derived && alloc && fetchBy === 'sektor') x.alloc = '1';
        if (cmpR) { x.cmp_from = cmpR[0]; x.cmp_to = cmpR[1]; }
        api<Profit>(`/pl-cube/profit?${qsOf(year, mode, path, from, to, { by: fetchBy, limit: '200', ...sortQs, ...x }, sc)}`, token)
            .then((p) => {
                if (dead) return;
                const cmp = p.cmp ? { ...p.cmp, total: normRow(p.cmp.total), rows: (p.cmp.rows ?? []).map(normRow) } : null;
                setData({ ...p, total: normRow(p.total), rows: (p.rows ?? []).map(normRow), rest: p.rest ? normRow(p.rest) : null, notes: p.notes ?? [], cmp });
            })
            .catch((e: Error) => { if (!dead) { setData(null); setErr(e.message); } })
            .finally(() => { if (!dead) setLoading(false); });
        return () => { dead = true; };
    }, [token, year, mode, path, from, to, fetchBy, tab, sortTouched, sort, sc, alloc, cmpR, derived]);

    // drawer se zatvara kad se promijeni cvor, os, razdoblje ili prikaz
    useEffect(() => { setDrawer(null); }, [year, mode, path, from, to, by, tab, sc]);

    const openDrawer = (r: Row) => {
        const step: Step = { dim: by, key: r.key };
        const qs = (extra: Record<string, string>) => qsOf(year, mode, [...path, step], from, to, extra, sc);
        setDrawer({ key: r.key, row: r, lines: null, konta: null, facts: null, err: null });
        setEvFact(null); setEv(null);
        const put = (patch: Partial<Drawer>) => setDrawer((d) => (d && d.key === r.key ? { ...d, ...patch } : d));
        const fail = (e: Error) => put({ err: e.message });
        api<Cube>(`/pl-cube/cube?${qs({ dims: 'pl_line,ym' })}`, token).then((c) => put({ lines: c })).catch(fail);
        api<Cube>(`/pl-cube/cube?${qs({ dims: 'konto3,pl_line' })}`, token).then((c) => put({ konta: c })).catch(fail);
        api<Facts>(`/pl-cube/facts?${qs({ limit: '30' })}`, token)
            .then((f) => put({ facts: { ...f, rows: (f.rows ?? []).map((x) => ({ ...x, signed: num(x.signed), confidence: num(x.confidence) })) } }))
            .catch(fail);
    };

    // redci ponude
    useEffect(() => {
        setFacts(null); setEvFact(null); setEv(null); setEvErr(null);
        if (!isOffer || tab !== 'drill') return undefined;
        let dead = false;
        api<Facts>(`/pl-cube/facts?${qsOf(year, mode, path, from, to, { limit: '200' }, sc)}`, token)
            .then((f) => { if (!dead) setFacts({ ...f, rows: (f.rows ?? []).map((r) => ({ ...r, signed: num(r.signed), confidence: num(r.confidence) })) }); })
            .catch((e: Error) => { if (!dead) setErr(e.message); });
        return () => { dead = true; };
    }, [token, year, mode, path, from, to, isOffer, tab, sc]);

    // matrica poslovnica x sektor (2.2) odnosno mjesto troska x izvedeni sektor (2.4), jedan upit
    const mxRow: Dim = derived ? 'mt' : 'branch_name';
    const mxCol: Dim = derived ? 'sektor' : 'business_type';
    const mxBase = (p: Step[]) => p.filter((s) => s.dim !== mxRow && s.dim !== mxCol && s.dim !== 'document');
    useEffect(() => {
        if (tab !== 'matrica') return undefined;
        let dead = false;
        setLoading(true); setErr(null);
        api<Cube>(`/pl-cube/cube?${qsOf(year, mode, mxBase(path), from, to, { dims: `${mxRow},${mxCol},pl_line` }, sc)}`, token)
            .then((c) => { if (!dead) setCube(c); })
            .catch((e: Error) => { if (!dead) { setCube(null); setErr(e.message); } })
            .finally(() => { if (!dead) setLoading(false); });
        return () => { dead = true; };
    }, [token, year, mode, path, from, to, tab, sc]);

    const changeBy = (d: Dim) => { setBy(d); setSortTouched(false); setSort({ col: d === 'ym' ? 'key' : 'ebitda', dir: d === 'ym' ? 1 : -1 }); };
    const drill = (key: string | null) => {
        const np = [...path, { dim: by, key }];
        setPath(np);
        if (by !== 'document') changeBy(nextDim(by, path, derived));
    };
    const goTo = (i: number) => {
        // i = broj koraka koji ostaju; os postaje os prvog uklonjenog koraka
        if (i >= path.length) return;
        const removed = path[i];
        setPath(path.slice(0, i));
        changeBy(removed.dim);
    };
    const up = useCallback(() => {
        if (evFact) { setEvFact(null); setEv(null); return; }
        if (drawer) { setDrawer(null); return; }
        if (path.length === 0) return;
        const removed = path[path.length - 1];
        setPath(path.slice(0, -1));
        setBy(removed.dim);
        setSortTouched(false);
        setSort({ col: removed.dim === 'ym' ? 'key' : 'ebitda', dir: removed.dim === 'ym' ? 1 : -1 });
    }, [evFact, drawer, path]);

    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); up(); } };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [up]);

    const openEvent = (f: Fact) => {
        setEvFact(f); setEv(null); setEvErr(null);
        api<EventInfo>(`/pl-cube/event?fact_id=${f.id}`, token)
            .then(setEv)
            .catch((e: Error) => setEvErr(e.message));
    };

    const sorted = useMemo(() => {
        const rows = [...(data?.rows ?? [])];
        const { col, dir } = sort;
        rows.sort((a, b) => {
            if (col === 'key') return dir * keyLabel(by, a.key).localeCompare(keyLabel(by, b.key), 'hr');
            const av = a[col] ?? -Infinity; const bv = b[col] ?? -Infinity;
            return dir * ((av as number) - (bv as number));
        });
        return rows;
    }, [data, sort, by, keyLabel]);
    const barMax = Math.max(1, ...sorted.map((r) => Math.abs(r.ebitda)));
    const hasNeg = sorted.some((r) => r.ebitda < 0);

    // matrica: redovi poslovnice, stupci sektori, celija = odabrana metrika
    const matrix = useMemo(() => {
        if (!cube) return null;
        const metricOk = (line: string) => (mxMetric === 'prihod' ? line === 'prihod' : mxMetric === 'bruto' ? line === 'prihod' || line === 'cogs' : EBITDA_LINES.has(line));
        const cells = new Map<string, number>(); const rowT = new Map<string, number>(); const colT = new Map<string, number>();
        let total = 0;
        cube.rows.forEach((r) => {
            const line = r.keys.pl_line ?? '';
            if (!metricOk(line)) return;
            const b = r.keys[mxRow] ?? NULL; const s = r.keys[mxCol] ?? NULL; const v = num(r.signed);
            cells.set(`${b}\u0001${s}`, (cells.get(`${b}\u0001${s}`) ?? 0) + v);
            rowT.set(b, (rowT.get(b) ?? 0) + v); colT.set(s, (colT.get(s) ?? 0) + v); total += v;
        });
        const cols = Array.from(colT.keys()).sort((a, b) => Math.abs(colT.get(b)!) - Math.abs(colT.get(a)!));
        const rows = Array.from(rowT.keys()).sort((a, b) => rowT.get(b)! - rowT.get(a)!);
        return { cells, rowT, colT, cols, rows, total };
    }, [cube, mxMetric, mxRow, mxCol]);

    const openCell = (branch: string, sector: string | null) => {
        const np: Step[] = [...mxBase(path)];
        if (sector !== null) np.push({ dim: mxCol, key: sector === NULL ? null : sector });
        np.push({ dim: mxRow, key: branch === NULL ? null : branch });
        setPath(np); changeBy('document'); setTab('drill');
    };

    const k = data?.total ?? null;
    const noEmployeeSource = by === 'employee_code' && !isOffer && (data?.rows ?? []).every((r) => r.key === null || r.key === '');
    const title = derived ? `${t('P&L kocka - explorer', 'P&L cube - explorer')} ${dFrom} - ${dTo}` : `${t('Ekonomski P&L', 'Economic P&L')} ${year}`;
    const modeLabel: Record<EkonMode, [string, string]> = { knjig: ['Faros knjizeni', 'Faros booked'], sim_knjig: ['Sim. knjig.', 'Sim. book'], ekon: ['Sim. ekonomski', 'Sim. economic'] };

    const th = (col: SortCol, label: string, cls = '') => (
        <th className={cls} onClick={() => { setSortTouched(true); setSort((s) => (s.col === col ? { col, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { col, dir: col === 'key' ? 1 : -1 })); }}
            aria-sort={sort.col === col ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
            {label}{sort.col === col ? (sort.dir === 1 ? ' ^' : ' v') : ''}
        </th>
    );
    const bar = (v: number) => {
        const w = (Math.abs(v) / barMax) * (hasNeg ? 50 : 100);
        const zero = hasNeg ? 50 : 0;
        return (
            <div className="track">
                <span className="zero" style={{ left: `${zero}%` }} />
                {v !== 0 ? <span className={`bar${v < 0 ? ' neg' : ''}`} style={{ left: `${v < 0 ? zero - w : zero}%`, width: `${Math.max(w, 0.5)}%` }} /> : null}
            </div>
        );
    };
    const cellsOf = (r: Row) => (
        <>
            <td>{fmtE(r.prihod)}</td>
            <td>{fmtE(r.cogs)}</td>
            <td>{fmtE(r.bruto)}</td>
            <td>{fmtP(r.bruto_pct)}</td>
            <td>{fmtE(r.opex)}</td>
            <td>{fmtE(r.ebitda)}</td>
            <td>{fmtP(r.ebitda_pct)}</td>
            <td>{r.n_docs.toLocaleString('hr-HR')}</td>
            <td title={r.gap_n > 0 ? `${r.gap_n} ${t('redaka bez osi', 'rows without axis')}` : ''}>{r.gap_n > 0 ? `${fmtE(r.gap_signed)} (${r.gap_n})` : '-'}</td>
        </>
    );
    const hasCmp = Boolean(derived && data?.cmp);
    const cmpMap = useMemo(() => new Map((data?.cmp?.rows ?? []).map((r) => [r.key ?? NULL, r] as [string, Row])), [data]);
    const cmpCells = (r: Row, c: Row | null | undefined) => (hasCmp ? (
        <>
            <td className="cmp">{c ? fmtE(c.prihod) : '-'}</td>
            <td className="cmp">{c ? fmtE(c.ebitda) : '-'}</td>
            <td className="cmp">{c ? fmtE(r.ebitda - c.ebitda) : '-'}</td>
        </>
    ) : null);
    const valStr = (v: unknown): string => {
        if (v === null || v === undefined || v === '') return '-';
        if (typeof v === 'object') return JSON.stringify(v);
        if (typeof v === 'number') return Number.isInteger(v) ? String(v) : fmtE2(v);
        return String(v);
    };
    const kvTable = (o: Record<string, unknown>) => (
        <table className="kv"><tbody>
            {Object.entries(o).map(([kk, v]) => <tr key={kk}><td>{kk}</td><td>{valStr(v)}</td></tr>)}
        </tbody></table>
    );
    const segOf = (f: Fact): string => {
        const pre = (f.source_ref ?? '').split(':')[0];
        if (SEGMENTS[pre]) return t(SEGMENTS[pre][0], SEGMENTS[pre][1]);
        return f.origin && f.origin !== 'booked' ? f.origin : (f.source_table ?? '');
    };

    const offerCard = () => {
        if (!facts) return <div className="empty">{err ? `${t('Greska', 'Error')}: ${err}` : `${t('ucitavam', 'loading')}...`}</div>;
        const rev = facts.rows.filter((f) => f.pl_line === 'prihod');
        const cogs = facts.rows.filter((f) => f.pl_line === 'cogs');
        const other = facts.rows.filter((f) => f.pl_line !== 'prihod' && f.pl_line !== 'cogs');
        const sum = (a: Fact[]) => a.reduce((s, f) => s + f.signed, 0);
        const sRev = sum(rev); const sCogs = sum(cogs); const marza = sRev + sCogs;
        const first = facts.rows[0];
        const dates = facts.rows.map((f) => f.doc_date ?? '').filter((d) => d !== '').sort();
        const group = (label: string, rows: Fact[], showSeg: boolean) => (
            <Fragment key={label}>
                <tr className="sub"><td className="lbl" colSpan={7}>{label}</td><td>{fmtE2(sum(rows))}</td><td /></tr>
                {rows.map((f) => (
                    <tr key={f.id} className={evFact?.id === f.id ? 'sel' : ''}>
                        <td className="lbl">{f.doc_date ?? ''}</td>
                        <td className="lbl">{showSeg ? <span className="tag">{segOf(f)}</span> : (f.business_type ? keyLabel('business_type', f.business_type) : '')}</td>
                        <td className="lbl">{f.source_ref ?? ''}</td>
                        <td className="lbl">{f.rule_code ?? ''}</td>
                        <td className="lbl">{f.origin}</td>
                        <td>{f.confidence}</td>
                        <td className="lbl">{f.coverage === 'gap' ? <span className="tag">{f.gap_reason ?? t('rupa', 'gap')}</span> : ''}</td>
                        <td>{fmtE2(f.signed)}</td>
                        <td><button type="button" className="small" onClick={() => openEvent(f)}>{t('Dokaz', 'Proof')}</button></td>
                    </tr>
                ))}
            </Fragment>
        );
        return (
            <div className="card">
                <h2>{t('Ponuda', 'Offer')} {docStep?.key ?? t(NULL_LABELS.document[0], NULL_LABELS.document[1])}</h2>
                <dl>
                    <dt>{t('Tvrtka', 'Company')}</dt><dd>{first?.company_code ?? '-'}</dd>
                    <dt>{t('Partner', 'Partner')}</dt><dd>{facts.rows.find((f) => f.partner)?.partner ?? '-'}</dd>
                    <dt>{t('Poslovnica', 'Branch')}</dt><dd>{keyLabel('branch_name', first?.branch_name ?? null)}</dd>
                    <dt>{t('Sektor', 'Sector')}</dt><dd>{keyLabel('business_type', rev[0]?.business_type ?? first?.business_type ?? null)}</dd>
                    <dt>{t('Datum', 'Date')}</dt><dd>{dates.length ? (dates[0] === dates[dates.length - 1] ? dates[0] : `${dates[0]} - ${dates[dates.length - 1]}`) : '-'}</dd>
                    <dt>{t('Prihod', 'Revenue')}</dt><dd>{fmtE2(sRev)} EUR</dd>
                    <dt>COGS</dt><dd>{fmtE2(sCogs)} EUR</dd>
                    <dt>{t('Bruto marza', 'Gross margin')}</dt><dd><b>{fmtE2(marza)} EUR</b> ({sRev ? fmtP(marza / Math.abs(sRev)) : '-'})</dd>
                    {other.length ? <><dt>{t('Ostale linije', 'Other lines')}</dt><dd>{fmtE2(sum(other))} EUR</dd></> : null}
                </dl>
                {facts.rows.length < facts.n ? <p className="muted">{t('prikazano', 'shown')} {facts.rows.length} {t('od', 'of')} {facts.n} {t('redaka (najveci po iznosu)', 'rows (largest by amount)')}</p> : null}
                <h2>{t('Redci ponude', 'Offer rows')}</h2>
                <table>
                    <thead><tr>
                        <th className="lbl nosort">{t('Datum', 'Date')}</th><th className="lbl nosort">{t('Segment / sektor', 'Segment / sector')}</th>
                        <th className="lbl nosort">{t('Izvor', 'Source ref')}</th><th className="lbl nosort">{t('Pravilo', 'Rule')}</th><th className="lbl nosort">{t('Porijeklo', 'Origin')}</th>
                        <th className="nosort">Conf.</th><th className="lbl nosort">{t('Rupa', 'Gap')}</th><th className="nosort">EUR</th><th className="nosort" />
                    </tr></thead>
                    <tbody>
                        {rev.length ? group(t('Prihod', 'Revenue'), rev, false) : null}
                        {cogs.length ? group(t('COGS po segmentima', 'COGS by segment'), cogs, true) : null}
                        {other.length ? group(t('Ostalo', 'Other'), other, false) : null}
                        <tr className="total"><td className="lbl" colSpan={7}>{t('Bruto marza ponude', 'Offer gross margin')}</td><td>{fmtE2(marza)}</td><td /></tr>
                    </tbody>
                </table>
                {facts.rows.length === 0 ? <div className="empty">{t('Nema redaka za ovu ponudu u odabranom razdoblju i prikazu.', 'No rows for this offer in the selected period and view.')}</div> : null}
            </div>
        );
    };

    const eventPanel = () => (evFact ? (
        <div className="side">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ flex: 1 }}>{t('Dokaz retka', 'Row proof')} #{evFact.id}</h2>
                <button type="button" className="small" onClick={() => { setEvFact(null); setEv(null); }}>{t('Zatvori (Esc)', 'Close (Esc)')}</button>
            </div>
            {evErr ? <p>{t('Greska', 'Error')}: {evErr}</p> : null}
            {!ev && !evErr ? <p className="muted">{t('ucitavam', 'loading')}...</p> : null}
            {ev ? (
                <>
                    <h2>{t('Pravilo', 'Rule')}</h2>
                    {ev.rule ? (
                        <dl>
                            <dt>{t('Sifra', 'Code')}</dt><dd>{ev.rule.code}</dd>
                            <dt>{t('Naziv', 'Title')}</dt><dd>{ev.rule.title}</dd>
                            {ev.rule.description ? <><dt>{t('Opis', 'Description')}</dt><dd>{ev.rule.description}</dd></> : null}
                            {ev.rule.mode ? <><dt>{t('Prikaz', 'Mode')}</dt><dd>{ev.rule.mode}</dd></> : null}
                        </dl>
                    ) : <p className="muted">{t('Knjizeni redak, bez simulacijskog pravila.', 'Booked row, no simulation rule.')}{evFact.rule_code ? ` (${evFact.rule_code})` : ''}</p>}
                    {ev.expected_posting && (ev.expected_posting.debit || ev.expected_posting.credit) ? (
                        <>
                            <h2>{t('Ocekivano knjizenje', 'Expected posting')}</h2>
                            <dl>
                                <dt>{t('Duguje', 'Debit')}</dt><dd>{ev.expected_posting.debit ?? '-'}</dd>
                                <dt>{t('Potrazuje', 'Credit')}</dt><dd>{ev.expected_posting.credit ?? '-'}</dd>
                                <dt>{t('Iznos', 'Amount')}</dt><dd>{fmtE2(num(ev.expected_posting.amount))} EUR</dd>
                            </dl>
                        </>
                    ) : null}
                    <h2>{t('Izvor', 'Source')}: {evFact.source_table} #{evFact.source_id}</h2>
                    {ev.source ? kvTable(ev.source) : <p className="muted">{t('Izvorni redak nije dostupan u drillu za ovu tablicu.', 'Source row is not available in drill for this table.')}</p>}
                    {ev.gl_postings_same_document?.length ? (
                        <>
                            <h2>{t('Knjizenja istog dokumenta (Faros GL)', 'Postings of the same document (Faros GL)')}</h2>
                            <table>
                                <thead><tr><th className="lbl nosort">{t('Konto', 'Account')}</th><th className="lbl nosort">{t('Datum', 'Date')}</th><th className="nosort">{t('Duguje', 'Debit')}</th><th className="nosort">{t('Potrazuje', 'Credit')}</th></tr></thead>
                                <tbody>{ev.gl_postings_same_document.map((p) => (
                                    <tr key={p.id}><td className="lbl">{p.konto}</td><td className="lbl">{p.datum_dokumenta ?? ''}</td><td>{num(p.duguje) ? fmtE2(num(p.duguje)) : ''}</td><td>{num(p.potrazuje) ? fmtE2(num(p.potrazuje)) : ''}</td></tr>
                                ))}</tbody>
                            </table>
                        </>
                    ) : null}
                    {ev.negated_fact ? (<><h2>{t('Negirani knjizeni redak', 'Negated booked row')}</h2>{kvTable(ev.negated_fact)}</>) : null}
                    {ev.fact ? (<details style={{ marginTop: 12 }}><summary className="muted">{t('Cijeli redak kocke', 'Full cube row')}</summary>{kvTable(ev.fact)}</details>) : null}
                </>
            ) : null}
        </div>
    ) : null);

    // m.2.4 drawer: klik na redak (samo derived) - KPI kljuca, P&L linije po mjesecima, konta, najveci redci s dokazom
    const drawerPanel = () => {
        if (!drawer || evFact) return null;
        const r = drawer.row;
        const lines = drawer.lines?.rows ?? [];
        const yms = Array.from(new Set(lines.map((x) => x.keys.ym ?? ''))).filter((y) => y !== '').sort();
        const plLines = Array.from(new Set(lines.map((x) => x.keys.pl_line ?? ''))).filter((l) => l !== '')
            .sort((a, b) => (LINE_ORDER.indexOf(a) + 100 * Number(!LINE_ORDER.includes(a))) - (LINE_ORDER.indexOf(b) + 100 * Number(!LINE_ORDER.includes(b))));
        const cell = new Map(lines.map((x) => [`${x.keys.pl_line}\u0001${x.keys.ym}`, num(x.signed)]));
        const lineTot = (l: string) => lines.filter((x) => x.keys.pl_line === l).reduce((s, x) => s + num(x.signed), 0);
        const ebitdaYm = (ym: string) => lines.filter((x) => x.keys.ym === ym && EBITDA_LINES.has(x.keys.pl_line ?? '')).reduce((s, x) => s + num(x.signed), 0);
        const konta = new Map<string, { v: number; lines: Set<string> }>();
        (drawer.konta?.rows ?? []).forEach((x) => {
            const kk = x.keys.konto3 ?? NULL; const e = konta.get(kk) ?? { v: 0, lines: new Set<string>() };
            e.v += num(x.signed); if (x.keys.pl_line) e.lines.add(x.keys.pl_line); konta.set(kk, e);
        });
        const kontaTop = Array.from(konta.entries()).sort((a, b) => Math.abs(b[1].v) - Math.abs(a[1].v)).slice(0, 15);
        const lineLbl = (l: string) => (LINE_LABELS[l] ? t(LINE_LABELS[l][0], LINE_LABELS[l][1]) : l);
        const monShort = (ym: string) => MONTHS[locale][Number(ym.slice(5, 7)) - 1]?.slice(0, 3) ?? ym;
        return (
            <div className="side drawer" role="dialog" aria-label={`${dimLabel(by)} ${keyLabel(by, drawer.key)}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h2 style={{ flex: 1 }}><span className="muted">{dimLabel(by)}</span><br />{keyLabel(by, drawer.key)}</h2>
                    {by !== 'document' ? <button type="button" className="small" onClick={() => { const kk = drawer.key; setDrawer(null); drill(kk); }}>{t('Razina nize', 'Drill down')} &gt;</button> : null}
                    <button type="button" className="small" onClick={() => setDrawer(null)}>{t('Zatvori (Esc)', 'Close (Esc)')}</button>
                </div>
                <dl>
                    <dt>{t('Prihod', 'Revenue')}</dt><dd>{fmtE2(r.prihod)} EUR</dd>
                    <dt>COGS</dt><dd>{fmtE2(r.cogs)} EUR</dd>
                    <dt>{t('Bruto marza', 'Gross margin')}</dt><dd><b>{fmtE2(r.bruto)} EUR</b> ({fmtP(r.bruto_pct)})</dd>
                    <dt>{t('Opex (s 491)', 'Opex (incl. 491)')}</dt><dd>{fmtE2(r.opex)} EUR</dd>
                    {r.ostalo ? <><dt>{t('Ostalo u EBITDA', 'Other in EBITDA')}</dt><dd>{fmtE2(r.ostalo)} EUR</dd></> : null}
                    <dt>EBITDA</dt><dd><b>{fmtE2(r.ebitda)} EUR</b> ({fmtP(r.ebitda_pct)})</dd>
                    <dt>{t('Udio u prihodu cvora', 'Share of node revenue')}</dt><dd>{k && Math.abs(k.prihod) >= 0.01 ? fmtP(r.prihod / Math.abs(k.prihod)) : '-'}</dd>
                    <dt>{t('Ponude / redaka', 'Offers / rows')}</dt><dd>{r.n_docs.toLocaleString('hr-HR')} / {r.n.toLocaleString('hr-HR')}</dd>
                    {r.gap_n ? <><dt>{t('Rupa', 'Gap')}</dt><dd>{fmtE2(r.gap_signed)} EUR ({r.gap_n})</dd></> : null}
                    <dt>{t('Min. pouzdanost', 'Min. confidence')}</dt><dd>{r.conf_min}</dd>
                </dl>
                {drawer.err ? <p>{t('Greska', 'Error')}: {drawer.err}</p> : null}
                <h2>{t('Linije P&L po mjesecima', 'P&L lines by month')}</h2>
                {!drawer.lines ? <p className="muted">{t('ucitavam', 'loading')}...</p> : (
                    <div className="scrollx">
                        <table className="dt">
                            <thead><tr><th className="lbl nosort">{t('Linija', 'Line')}</th>{yms.map((y) => <th key={y} className="nosort">{monShort(y)}</th>)}<th className="nosort">{t('Ukupno', 'Total')}</th></tr></thead>
                            <tbody>
                                {plLines.map((l) => (
                                    <tr key={l}><td className="lbl">{lineLbl(l)}</td>{yms.map((y) => { const v = cell.get(`${l}\u0001${y}`); return <td key={y} className={v !== undefined && v < 0 ? 'neg' : ''}>{v === undefined ? '' : fmtE(v)}</td>; })}<td><b>{fmtE(lineTot(l))}</b></td></tr>
                                ))}
                                <tr className="total"><td className="lbl">EBITDA</td>{yms.map((y) => <td key={y}>{fmtE(ebitdaYm(y))}</td>)}<td>{fmtE(yms.reduce((s, y) => s + ebitdaYm(y), 0))}</td></tr>
                            </tbody>
                        </table>
                    </div>
                )}
                <h2>{t('Konta (najvecih 15)', 'Accounts (top 15)')}</h2>
                {!drawer.konta ? <p className="muted">{t('ucitavam', 'loading')}...</p> : (
                    <table className="dt">
                        <thead><tr><th className="lbl nosort">{t('Konto', 'Account')}</th><th className="lbl nosort">{t('Linija', 'Line')}</th><th className="nosort">EUR</th></tr></thead>
                        <tbody>{kontaTop.map(([kk, e]) => (
                            <tr key={kk}><td className="lbl">{kk === NULL ? t('bez konta', 'no account') : kk}</td><td className="lbl">{Array.from(e.lines).map(lineLbl).join(', ')}</td><td>{fmtE(e.v)}</td></tr>
                        ))}</tbody>
                    </table>
                )}
                <h2>{t('Najveci redci', 'Largest rows')}{drawer.facts ? ` (${drawer.facts.rows.length} ${t('od', 'of')} ${drawer.facts.n.toLocaleString('hr-HR')})` : ''}</h2>
                {!drawer.facts ? <p className="muted">{t('ucitavam', 'loading')}...</p> : (
                    <table className="dt">
                        <thead><tr>
                            <th className="lbl nosort">{t('Datum', 'Date')}</th><th className="lbl nosort">{t('Dokument', 'Document')}</th><th className="lbl nosort">{t('Linija', 'Line')}</th>
                            <th className="lbl nosort">{derived ? t('Sektor / MT', 'Sector / CC') : t('Poslovnica', 'Branch')}</th><th className="nosort">EUR</th><th className="nosort" />
                        </tr></thead>
                        <tbody>{drawer.facts.rows.map((f) => (
                            <tr key={f.id}>
                                <td className="lbl">{f.doc_date ?? ''}</td>
                                <td className="lbl" title={f.partner ?? ''}>{f.document ?? '-'}</td>
                                <td className="lbl">{lineLbl(f.pl_line)}{f.coverage === 'gap' ? <span className="tag">{f.gap_reason ?? t('rupa', 'gap')}</span> : null}</td>
                                <td className="lbl">{derived && f.derived ? `${keyLabel('sektor', f.derived.sektor)} / ${keyLabel('mt', f.derived.mt)}` : keyLabel('branch_name', f.branch_name)}</td>
                                <td className={f.signed < 0 ? 'neg' : ''}>{fmtE2(f.signed)}</td>
                                <td><button type="button" className="small" onClick={() => openEvent(f)}>{t('Dokaz', 'Proof')}</button></td>
                            </tr>
                        ))}</tbody>
                    </table>
                )}
            </div>
        );
    };

    const exportCsv = () => {
        const head = [dimLabel(by), t('Prihod', 'Revenue'), 'COGS', t('Bruto marza', 'Gross margin'), t('Marza %', 'Margin %'), 'Opex', t('Ostalo', 'Other'), 'EBITDA', 'EBITDA %', 'D&A', t('Ponude', 'Offers'), t('Rupa', 'Gap'), t('Min. pouzdanost', 'Min. confidence')];
        if (hasCmp) head.push(t('Prihod usp.', 'Revenue cmp.'), t('EBITDA usp.', 'EBITDA cmp.'), t('Razlika EBITDA', 'EBITDA change'));
        const line = (label: string, r: Row, c?: Row | null) => {
            const out: (string | number | null)[] = [label, r.prihod, r.cogs, r.bruto, r.bruto_pct === null ? null : r.bruto_pct * 100, r.opex, r.ostalo, r.ebitda, r.ebitda_pct === null ? null : r.ebitda_pct * 100, r.da, r.n_docs, r.gap_signed, r.conf_min];
            if (hasCmp) out.push(c ? c.prihod : null, c ? c.ebitda : null, c ? r.ebitda - c.ebitda : null);
            return out;
        };
        const rows = sorted.map((r) => line(keyLabel(by, r.key), r, cmpMap.get(r.key ?? NULL)));
        if (data?.rest) rows.push(line(`${t('Ostali', 'Others')} (${Math.max(0, (data.count ?? 0) - data.rows.length)})`, data.rest));
        if (k) rows.push(line(t('Ukupno', 'Total'), k, data?.cmp?.total));
        const crumbs = path.map((s) => keyLabel(s.dim, s.key)).join(' - ');
        const per = derived ? `${dFrom}_${dTo}${cos.length ? `_${cos.join('-')}` : ''}` : `${year}-${String(from).padStart(2, '0')}-${String(to).padStart(2, '0')}`;
        const safe = `${derived ? 'pl24' : 'pl22'}_${mode}_${per}_${by}${crumbs ? `_${crumbs}` : ''}`.replace(/[^\w.-]+/g, '_').slice(0, 120);
        downloadCsv(`${safe}.csv`, head, rows);
    };

    const gapHelp = t('Rupa = iznos koji je u zbroju, ali mu os (poslovnica, sektor, djelatnik) nije poznata. Nije nula, nego nerasporedeno; zato zbroj djece moze biti manji od roditelja na toj osi.',
        'Gap = amount that is in the total but whose axis (branch, sector, employee) is unknown. Not zero but unallocated; children may not add up to the parent on that axis.');

    return (
        <div className="ek-x">
            <style>{css}</style>
            <div className="head">
                <div className="top">
                    <button type="button" onClick={onExit}>{t('Izlaz', 'Exit')}</button>
                    <h1>{title}</h1>
                    <span className="muted">EUR{data?.build?.built_at ? ` | ${t('izgradjeno', 'built')} ${data.build.built_at}` : ''}{loading ? ` | ${t('ucitavam', 'loading')}...` : ''}</span>
                    <span className="grow" />
                    <span className="seg">
                        <button type="button" className={tab === 'drill' ? 'on' : ''} onClick={() => setTab('drill')}>{t('Drill', 'Drill')}</button>
                        <button type="button" className={tab === 'matrica' ? 'on' : ''} onClick={() => setTab('matrica')}>{t('Matrica', 'Matrix')}</button>
                    </span>
                    {tab === 'drill' && !isOffer ? <button type="button" onClick={exportCsv} disabled={!data || data.rows.length === 0} title={t('Tablica kakva je na ekranu, s retkom Ostali i Ukupno', 'Table as shown, with Others and Total rows')}>CSV</button> : null}
                    {onStatement ? <button type="button" onClick={onStatement}>{t('Iskaz', 'Statement')}</button> : null}
                    {onCube ? <button type="button" onClick={onCube}>{t('Kocka', 'Cube')}</button> : null}
                </div>
                <nav className="crumbs" aria-label={t('Razina', 'Level')}>
                    <button type="button" className={`c${path.length === 0 ? ' cur' : ''}`} onClick={() => goTo(0)} disabled={path.length === 0}>
                        <span className="d">{t('Grupa', 'Group')}</span>Auro Domus
                    </button>
                    {path.map((s, i) => (
                        <Fragment key={`${s.dim}-${i}`}>
                            <span className="sep">&gt;</span>
                            <button type="button" className={`c${i === path.length - 1 ? ' cur' : ''}`} onClick={() => goTo(i + 1)} disabled={i === path.length - 1}>
                                <span className="d">{dimLabel(s.dim)}</span>{keyLabel(s.dim, s.key)}
                            </button>
                        </Fragment>
                    ))}
                    {path.length ? <span className="muted" style={{ marginLeft: 12 }}>{t('Esc = razina gore', 'Esc = level up')}</span> : null}
                </nav>
                <div className="ctl">
                    {tab === 'drill' && !isOffer ? (
                        <span><span className="lab">{t('Grupiraj po', 'Group by')}</span>
                            <span className="seg">{groupChoices.map((d) => (
                                <button key={d} type="button" className={by === d ? 'on' : ''} disabled={path.some((s) => s.dim === d)} onClick={() => changeBy(d)}>{dimLabel(d)}</button>
                            ))}</span>
                        </span>
                    ) : null}
                    {tab === 'matrica' ? (
                        <span><span className="lab">{t('Celija', 'Cell')}</span>
                            <span className="seg">
                                <button type="button" className={mxMetric === 'prihod' ? 'on' : ''} onClick={() => setMxMetric('prihod')}>{t('Prihod', 'Revenue')}</button>
                                <button type="button" className={mxMetric === 'bruto' ? 'on' : ''} onClick={() => setMxMetric('bruto')}>{t('Bruto marza', 'Gross margin')}</button>
                                <button type="button" className={mxMetric === 'ebitda' ? 'on' : ''} onClick={() => setMxMetric('ebitda')}>{t('Doprinos (EBITDA linije)', 'Contribution (EBITDA lines)')}</button>
                            </span>
                        </span>
                    ) : null}
                    {derived ? (
                        <>
                            <span><span className="lab">{t('Tvrtke', 'Companies')}</span>
                                <span className="seg">
                                    <button type="button" className={cos.length === 0 ? 'on' : ''} onClick={() => setCos([])} title={t('Cijela grupa', 'Whole group')}>{t('Sve', 'All')}</button>
                                    {COMPANIES.map(([c, l]) => (
                                        <button key={c} type="button" className={cos.includes(c) ? 'on' : ''}
                                            onClick={() => setCos((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]))}>{l}</button>
                                    ))}
                                </span>
                            </span>
                            <span><span className="lab">{t('Razdoblje', 'Period')}</span>
                                <input type="date" value={dFrom} max={dTo} aria-label={t('od datuma', 'from date')} onChange={(e) => { const v = e.target.value; if (isIso(v)) { setDFrom(v); if (v > dTo) setDTo(v); } }} />{' - '}
                                <input type="date" value={dTo} min={dFrom} aria-label={t('do datuma', 'to date')} onChange={(e) => { const v = e.target.value; if (isIso(v)) { setDTo(v); if (v < dFrom) setDFrom(v); } }} />
                            </span>
                            <span title={t('Medudrustveno = konta pl_cube.ic_konto (ADO 457, ADMS 782), obje strane. Eliminacija vrijedi kad je u pogledu vise tvrtki.', 'Intercompany = accounts pl_cube.ic_konto (ADO 457, ADMS 782), both sides. Elimination applies when several companies are in view.')}>
                                <span className="lab">{t('Medudrustveno', 'Intercompany')}</span>
                                <span className="seg">
                                    {([['eliminate', t('Eliminiraj', 'Eliminate')], ['include', t('Ukljuci', 'Include')], ['isolate', t('Samo IC', 'IC only')]] as Array<[IcMode, string]>).map(([m, l]) => (
                                        <button key={m} type="button" className={ic === m ? 'on' : ''} onClick={() => setIc(m)}>{l}</button>
                                    ))}
                                </span>
                            </span>
                            <span><span className="lab">{t('Usporedba', 'Compare')}</span>
                                <select value={cmpMode} onChange={(e) => setCmpMode(e.target.value as CmpMode)} aria-label={t('usporedba razdoblja', 'period comparison')}>
                                    <option value="none">{t('bez', 'none')}</option>
                                    <option value="py">{t('isto razdoblje prosle godine', 'same period last year')}</option>
                                    <option value="prev">{t('prethodno razdoblje iste duljine', 'previous period of same length')}</option>
                                    <option value="custom">{t('slobodno', 'custom')}</option>
                                </select>
                                {cmpMode === 'custom' ? (
                                    <>{' '}
                                        <input type="date" value={cmpFrom} aria-label={t('usporedba od', 'compare from')} onChange={(e) => setCmpFrom(e.target.value)} />{' - '}
                                        <input type="date" value={cmpTo} aria-label={t('usporedba do', 'compare to')} onChange={(e) => setCmpTo(e.target.value)} />
                                    </>
                                ) : cmpR ? <span className="muted"> {cmpR[0]} - {cmpR[1]}</span> : null}
                            </span>
                            {by === 'sektor' && tab === 'drill' ? (
                                <span title={t('Overhead (Uprava, marketing, najam, logistika) na sektore po ekonomskom RUC-u, isto kao 2.3', 'Overhead (HQ, marketing, rent, logistics) to sectors by economic gross profit, same as 2.3')}>
                                    <span className="lab">{t('Alokacija', 'Allocation')}</span>
                                    {mode === 'knjig' ? <span className="muted">{t('knjigovodstveno je bez alokacija', 'booked view has no allocation')}</span> : (
                                        <span className="seg">
                                            <button type="button" className={!alloc ? 'on' : ''} onClick={() => setAlloc(false)}>{t('Prije', 'Before')}</button>
                                            <button type="button" className={alloc ? 'on' : ''} onClick={() => setAlloc(true)}>{t('Poslije', 'After')}</button>
                                        </span>
                                    )}
                                </span>
                            ) : null}
                        </>
                    ) : (
                    <span><span className="lab">{t('Razdoblje', 'Period')}</span>
                        <select value={from} onChange={(e) => { const v = Number(e.target.value); setFrom(v); if (to < v) setTo(v); }} aria-label={t('od mjeseca', 'from month')}>
                            {MONTHS[locale].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>{' - '}
                        <select value={to} onChange={(e) => { const v = Number(e.target.value); setTo(v); if (from > v) setFrom(v); }} aria-label={t('do mjeseca', 'to month')}>
                            {MONTHS[locale].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                    </span>
                    )}
                    <span><span className="lab">{t('Godina', 'Year')}</span>
                        <select value={year} onChange={(e) => onYear?.(Number(e.target.value))} disabled={!onYear}>
                            {(years?.length ? years : [year]).map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </span>
                    <span><span className="lab">{t('Prikaz', 'Mode')}</span>
                        <span className="seg">{(['knjig', 'sim_knjig', 'ekon'] as EkonMode[]).map((m) => (
                            <button key={m} type="button" className={mode === m ? 'on' : ''} disabled={!onMode} onClick={() => onMode?.(m)}>{t(modeLabel[m][0], modeLabel[m][1])}</button>
                        ))}</span>
                    </span>
                </div>
            </div>
            {tab === 'drill' ? (
                <>
                    <div className="kpi">
                        <div><div className="l">{t('Prihod', 'Revenue')}</div><div className="v">{k ? fmtM(k.prihod) : '-'}</div><div className="d">{k ? (hasCmp && data?.cmp ? `${t('usp.', 'cmp.')} ${fmtM(data.cmp.total.prihod)}` : `${fmtE(k.prihod)} EUR`) : ''}</div></div>
                        <div><div className="l">COGS</div><div className="v">{k ? fmtM(k.cogs) : '-'}</div><div className="d">{k ? `${fmtE(k.cogs)} EUR` : ''}</div></div>
                        <div><div className="l">{t('Bruto marza', 'Gross margin')}</div><div className="v">{k ? fmtM(k.bruto) : '-'}</div><div className="d">{k ? fmtP(k.bruto_pct) : ''}</div></div>
                        <div><div className="l" title={t('Opex + prijenos troska 491', 'Opex + cost transfer 491')}>{t('Troskovi lokacije', 'Location costs')}</div><div className="v">{k ? fmtM(k.opex) : '-'}</div><div className="d">{k && k.ostalo ? `${t('ostalo', 'other')} ${fmtE(k.ostalo)}` : ''}</div></div>
                        <div><div className="l">EBITDA</div><div className="v">{k ? fmtM(k.ebitda) : '-'}</div><div className="d">{k ? `${fmtP(k.ebitda_pct)}${hasCmp && data?.cmp ? ` | ${t('usp.', 'cmp.')} ${fmtM(data.cmp.total.ebitda)}` : ''}` : ''}</div></div>
                        <div><div className="l">{t('Broj ponuda', 'Offers')}</div><div className="v">{k ? k.n_docs.toLocaleString('hr-HR') : '-'}</div><div className="d">{k ? `${k.n.toLocaleString('hr-HR')} ${t('redaka', 'rows')}` : ''}</div></div>
                        <div title={gapHelp}><div className="l"><span className="help">{t('Rupa', 'Gap')}</span></div><div className="v">{k ? fmtM(k.gap_signed) : '-'}</div><div className="d">{k ? `${k.gap_n.toLocaleString('hr-HR')} ${t('redaka bez osi', 'rows without axis')}` : ''}</div></div>
                        <div title={t('Najniza pouzdanost simuliranog retka u cvoru (100 = knjizeno)', 'Lowest confidence of a simulated row in this node (100 = booked)')}><div className="l">{t('Min. pouzdanost', 'Min. confidence')}</div><div className="v">{k ? k.conf_min : '-'}</div><div className="d">{t('100 = knjizeno', '100 = booked')}</div></div>
                    </div>
                    {(data?.notes?.length || err) ? (
                        <div className="notes">
                            {err ? <div>{t('Greska', 'Error')}: {err}</div> : null}
                            {(data?.notes ?? []).map((n, i) => <div key={i}>{n}</div>)}
                        </div>
                    ) : null}
                    <div className="body">
                        <div className="main">
                            {isOffer ? offerCard() : (
                                <>
                                    {by === 'employee_code' ? (
                                        <div className="notice">{t('Os djelatnik: nema izvora. Faros izvjestaji Prodaja po djelatniku i Otkup po djelatniku jos nisu uvezeni; iznos je rupa, ne nula.', 'Employee axis: no source. Faros reports Sales by employee and Buyback by employee are not imported yet; the amount is a gap, not zero.')}</div>
                                    ) : null}
                                    {noEmployeeSource ? null : (
                                        <table>
                                            <thead><tr>
                                                {th('key', dimLabel(by), 'lbl')}
                                                {th('prihod', t('Prihod', 'Revenue'))}
                                                {th('cogs', 'COGS')}
                                                {th('bruto', t('Bruto marza', 'Gross margin'))}
                                                {th('bruto_pct', t('Marza %', 'Margin %'))}
                                                {th('opex', t('Opex (lokacija)', 'Opex (location)'))}
                                                {th('ebitda', 'EBITDA')}
                                                {th('ebitda_pct', 'EBITDA %')}
                                                {th('n_docs', t('Ponude', 'Offers'))}
                                                {th('gap_signed', t('Rupa', 'Gap'))}
                                                {hasCmp ? (
                                                    <>
                                                        <th className="nosort cmp" title={`${data?.cmp?.from} - ${data?.cmp?.to}`}>{t('Prihod usp.', 'Revenue cmp.')}</th>
                                                        <th className="nosort cmp" title={`${data?.cmp?.from} - ${data?.cmp?.to}`}>{t('EBITDA usp.', 'EBITDA cmp.')}</th>
                                                        <th className="nosort cmp">{t('Razlika EBITDA', 'EBITDA change')}</th>
                                                    </>
                                                ) : null}
                                                <th className="nosort barcell lbl">EBITDA</th>
                                                {derived ? <th className="nosort" aria-label={t('Razina nize', 'Drill down')} /> : null}
                                            </tr></thead>
                                            <tbody>
                                                {sorted.map((r) => {
                                                    // 2.4: klik otvara drawer, strelica ide razinu nize; 2.2: klik ide razinu nize (nepromijenjeno)
                                                    const act = () => (derived ? openDrawer(r) : drill(r.key));
                                                    const isSel = derived && drawer !== null && drawer.key === r.key;
                                                    return (
                                                        <tr key={r.key ?? NULL} className={`row${isSel ? ' sel' : ''}`} tabIndex={0} onClick={act}
                                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(); } else if (derived && e.key === 'ArrowRight' && by !== 'document') { e.preventDefault(); drill(r.key); } }}>
                                                            <td className="lbl" title={keyLabel(by, r.key)}>{keyLabel(by, r.key)}{r.conf_min < 100 ? <span className="tag" title={t('najniza pouzdanost', 'lowest confidence')}>{r.conf_min}</span> : null}</td>
                                                            {cellsOf(r)}
                                                            {cmpCells(r, cmpMap.get(r.key ?? NULL))}
                                                            <td className="barcell">{bar(r.ebitda)}</td>
                                                            {derived ? (
                                                                <td className="go">{by !== 'document' ? (
                                                                    <button type="button" className="small" title={t('Razina nize (strelica desno)', 'Drill down (right arrow)')}
                                                                        onClick={(e) => { e.stopPropagation(); drill(r.key); }}>&gt;</button>
                                                                ) : null}</td>
                                                            ) : null}
                                                        </tr>
                                                    );
                                                })}
                                                {data?.rest ? (
                                                    <tr className="rest">
                                                        <td className="lbl">{t('Ostali', 'Others')} ({Math.max(0, (data.count ?? 0) - data.rows.length)})</td>
                                                        {cellsOf(data.rest)}
                                                        {cmpCells(data.rest, null)}
                                                        <td className="barcell" />
                                                        {derived ? <td /> : null}
                                                    </tr>
                                                ) : null}
                                                {k ? (
                                                    <tr className="total"><td className="lbl">{t('Ukupno', 'Total')}</td>{cellsOf(k)}{cmpCells(k, data?.cmp?.total)}<td className="barcell" />{derived ? <td /> : null}</tr>
                                                ) : null}
                                            </tbody>
                                        </table>
                                    )}
                                    {data && data.count > data.rows.length ? (
                                        <p className="muted" style={{ padding: '6px 16px' }}>{t('prikazano', 'shown')} {data.rows.length} {t('od', 'of')} {data.count} ({sortTouched
                                            ? t('prvih po odabranom stupcu nad svim kljucevima; ostatak u retku Ostali', 'first by the selected column over all keys; the rest in the Others row')
                                            : t('najvecih po apsolutnoj EBITDA; ostatak u retku Ostali', 'largest by absolute EBITDA; the rest in the Others row')})</p>
                                    ) : null}
                                    {data && data.rows.length === 0 && !loading ? <div className="empty">{t('Nema podataka za ovaj cvor, razdoblje i prikaz.', 'No data for this node, period and mode.')}</div> : null}
                                </>
                            )}
                        </div>
                        {drawerPanel()}
                        {eventPanel()}
                    </div>
                </>
            ) : (
                <div className="body">
                    <div className="main mx">
                        {err ? <div className="notes"><div>{t('Greska', 'Error')}: {err}</div></div> : null}
                        <p className="muted" style={{ padding: '6px 16px', margin: 0 }}>
                            {derived
                                ? t('Redovi mjesta troska (poslovnice, odjeli Uprave, vozila), stupci izvedeni sektori kocke. Klik na celiju otvara dokumente tog MT-a i sektora.', 'Rows cost centres (branches, HQ departments, vehicles), columns derived cube sectors. Click a cell to open documents of that cost centre and sector.')
                                : t('Redovi poslovnice, stupci sektori. Klik na celiju otvara ponude te poslovnice i sektora. Troskovi bez sektora (poslovnice, Uprava) stoje u stupcu (bez sektora).', 'Rows branches, columns sectors. Click a cell to open offers of that branch and sector. Costs without sector (branches, HQ) sit in the (no sector) column.')}
                            {cube?.truncated ? ` ${t('Rezultat je skracen na strani servera.', 'Result truncated server-side.')}` : ''}
                        </p>
                        {matrix && matrix.rows.length ? (
                            <table>
                                <thead><tr>
                                    <th className="lbl nosort">{dimLabel(mxRow)}</th>
                                    {matrix.cols.map((c) => <th key={c} className="nosort">{keyLabel(mxCol, c === NULL ? null : c)}</th>)}
                                    <th className="nosort">{t('Ukupno', 'Total')}</th>
                                </tr></thead>
                                <tbody>
                                    {matrix.rows.map((b) => (
                                        <tr key={b}>
                                            <td className="lbl">{keyLabel(mxRow, b === NULL ? null : b)}</td>
                                            {matrix.cols.map((c) => {
                                                const v = matrix.cells.get(`${b}\u0001${c}`);
                                                return v === undefined ? <td key={c} /> : (
                                                    <td key={c} className={`cell${v < 0 ? ' neg' : ''}`} tabIndex={0} onClick={() => openCell(b, c)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') openCell(b, c); }}>{fmtE(v)}</td>
                                                );
                                            })}
                                            <td className="cell" onClick={() => openCell(b, null)}><b>{fmtE(matrix.rowT.get(b) ?? 0)}</b></td>
                                        </tr>
                                    ))}
                                    <tr className="total">
                                        <td className="lbl">{t('Ukupno', 'Total')}</td>
                                        {matrix.cols.map((c) => <td key={c}>{fmtE(matrix.colT.get(c) ?? 0)}</td>)}
                                        <td>{fmtE(matrix.total)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (!loading ? <div className="empty">{t('Nema podataka.', 'No data.')}</div> : null)}
                    </div>
                </div>
            )}
        </div>
    );
}

export default UpravaEkonExplorer;
